/**
 * DropZone — the "drop in any url or screenshot" smart-ingest UI.
 *
 * Accepts:
 *   - Drag/drop any file (image, PDF, etc.) → uploads as image (best effort).
 *   - Paste from clipboard: image → image; URL or text → posted as such.
 *   - Typed URL or text in the textarea.
 *
 * Routes to one of three backends depending on the selected kind:
 *   - `auto` / `event`: POST /api/mgmt/ingest (event extraction pipeline)
 *   - `venue`: POST /api/mgmt/submit-venue (venue match-or-create + social enrichment + website/IG/FB scans)
 *   - `series` / `organizer`: POST /api/mgmt/submit-series (series catalog)
 *
 * After an auto/event URL submission, the user can also one-click "Add as
 * venue" using the same URL — useful when the URL is a venue homepage that
 * the event extractor couldn't parse as a single event.
 */

import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { api } from '../lib/api';
import { useIsAdmin } from '../lib/auth';

const URL_RE = /^https?:\/\/\S+$/i;

/**
 * Turn a thrown API error into a user-friendly message. The everyone-submit
 * endpoint (`/api/submit`) returns 429 with a JSON `{ message }` body when a
 * non-admin exceeds their daily cap — surface that instead of the raw
 * `API 429: {...}` string.
 */
function formatSubmitError(e: unknown): string {
  const err = e as Error & { status?: number };
  if (err?.status === 429) {
    const m = /API 429:\s*(\{.*\})/s.exec(err.message || '');
    if (m) {
      try {
        const parsed = JSON.parse(m[1]) as { message?: string };
        if (parsed?.message) return parsed.message;
      } catch {
        /* fall through */
      }
    }
    return 'You’ve reached the daily submission limit. Please try again tomorrow.';
  }
  return err?.message ?? String(e);
}

interface IngestResponse { id: string; detected: string[] }
interface SubmitVenueResponse {
  venueId: string;
  name?: string;
  created: boolean;
  viaPlaces: boolean;
  websiteScanIngested?: number;
  websiteScanError?: string;
  websiteScanSkippedReason?: string;
  igScanIngested?: number;
  igScanError?: string;
  igScanSkippedReason?: string;
  fbScanIngested?: number;
  fbScanError?: string;
  fbScanSkippedReason?: string;
}
interface SubmitSeriesResponse {
  seriesId: string;
  type: 'recurring-event' | 'aggregator' | 'organizer';
  name?: string;
  status: 'candidate' | 'active' | 'dismissed';
  created: boolean;
  instagramHandle?: string;
  facebookUrl?: string;
  website?: string;
}
interface ComposeEventResponse {
  rawId: string;
  eventCount: number;
  recurringSeriesId?: string;
  events: Array<{ partitionKey: string; rowKey: string; startUtc: string; isRecurringInstance: boolean }>;
  venuesCreated: Array<{ id: string; name: string; neighborhood?: string }>;
  seriesCreated: Array<{ id: string; name: string; type?: string }>;
  agentMessage?: string;
  agentTrace: Array<{ toolName: string; argsJson: string; resultJson: string }>;
}

type Kind = 'auto' | 'event' | 'venue' | 'series' | 'organizer' | 'compose';
type SeriesType = 'recurring-event' | 'aggregator' | 'organizer';

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  // chunked to avoid call-stack overflow on large files
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export default function DropZone({ onIngested }: { onIngested?: (r: IngestResponse) => void }) {
  const isAdmin = useIsAdmin();
  // Admins post to the trusted admin-ingest endpoint (full options, no cap,
  // normal auto-publish pipeline). Non-admins post to the guarded everyone
  // endpoint (per-user daily cap + forced review). Venue/series/compose kinds
  // are admin-only and hidden below for non-admins.
  const eventIngestPath = isAdmin ? '/api/mgmt/ingest' : '/api/submit';
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);
  const [hint, setHint] = useState('');
  const [kind, setKind] = useState<Kind>('auto');
  /** Sub-type for series submissions (recurring-event vs aggregator). Ignored for `organizer` kind. */
  const [seriesType, setSeriesType] = useState<Exclude<SeriesType, 'organizer'>>('recurring-event');
  // Compose-mode state. Recurrence override is optional — when no fields are
  // touched the agent decides recurrence based on the natural-language
  // instructions. The "Continue indefinitely" checkbox is the most common
  // override (the agent can’t always tell "every Saturday" from "every
  // Saturday until I delete it" without context).
  const [composeRecurrence, setComposeRecurrence] = useState<'' | 'one-time' | 'weekly' | 'biweekly' | 'monthly'>('');
  const [composePermanent, setComposePermanent] = useState(false);
  const [composeDays, setComposeDays] = useState<string[]>([]);
  const [showComposeAdvanced, setShowComposeAdvanced] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  /** Last URL submitted via auto/event path — surfaced as an "Add as venue?" CTA. */
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const submitFile = useCallback(async (file: File) => {
    setBusy(true); setMsg(null); setLastUrl(null);
    try {
      const dataBase64 = await fileToBase64(file);
      const r = await api.post<IngestResponse>(eventIngestPath, {
        image: { dataBase64, filename: file.name, contentType: file.type || 'application/octet-stream' },
        hint: hint || undefined
      });
      setMsg({ kind: 'ok', text: `Queued for extraction (${r.detected.join(', ')}).` });
      setValue(''); setHint('');
      onIngested?.(r);
    } catch (e) {
      setMsg({ kind: 'err', text: formatSubmitError(e) });
    } finally {
      setBusy(false);
    }
  }, [hint, onIngested, eventIngestPath]);

  const submitAsVenue = useCallback(async (url?: string, name?: string) => {
    const u = (url ?? '').trim();
    const n = (name ?? '').trim();
    if (!u && !n) {
      setMsg({ kind: 'err', text: 'Provide a venue URL or name.' });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<SubmitVenueResponse>('/api/mgmt/submit-venue', {
        url: u || undefined,
        name: n || undefined,
        hint: hint || undefined
      });
      const verb = r.created ? 'Created venue' : 'Matched existing venue';
      const places = r.viaPlaces ? ' (via Google Places)' : '';
      const scanParts: string[] = [];
      if (typeof r.websiteScanIngested === 'number') scanParts.push(`website: ${r.websiteScanIngested}`);
      if (typeof r.igScanIngested === 'number') scanParts.push(`IG: ${r.igScanIngested}`);
      if (typeof r.fbScanIngested === 'number') scanParts.push(`FB: ${r.fbScanIngested}`);
      const scan = scanParts.length > 0 ? ` — scanned (${scanParts.join(', ')} event(s) queued)` : '';
      setMsg({ kind: 'ok', text: `${verb}: ${r.name ?? r.venueId}${places}${scan}.` });
      setValue(''); setHint(''); setLastUrl(null);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [hint]);

  const submitAsSeries = useCallback(async (
    url: string | undefined,
    name: string | undefined,
    type: SeriesType
  ) => {
    const u = (url ?? '').trim();
    const n = (name ?? '').trim();
    if (!u && !n) {
      setMsg({ kind: 'err', text: 'Provide a URL or name.' });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<SubmitSeriesResponse>('/api/mgmt/submit-series', {
        url: u || undefined,
        name: n || undefined,
        type,
        hint: hint || undefined
      });
      const label = r.type === 'organizer' ? 'organizer' : (r.type === 'aggregator' ? 'aggregator' : 'series');
      const verb = r.created ? `Created ${label}` : `Matched existing ${label}`;
      const handle = r.instagramHandle ? ` (@${r.instagramHandle})` : '';
      setMsg({ kind: 'ok', text: `${verb}: ${r.name ?? r.seriesId}${handle} — status: ${r.status}.` });
      setValue(''); setHint('');
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [hint]);

  const submitCompose = useCallback(async (
    instructions: string,
    sourceUrl?: string
  ) => {
    const body: Record<string, unknown> = {
      instructions,
      sourceUrl: sourceUrl || undefined
    };
    // Build recurrence override only when the admin actually picked something
    // \u2014 otherwise let the agent infer recurrence from the prose.
    if (composeRecurrence || composePermanent) {
      body.recurrenceOverride = {
        pattern: composeRecurrence || undefined,
        daysOfWeek: composeDays.length ? composeDays : undefined,
        permanent: composePermanent || undefined
      };
    }
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<ComposeEventResponse>('/api/mgmt/compose-event', body);
      const venueNote = r.venuesCreated.length > 0
        ? ` Created venue: ${r.venuesCreated.map((v) => v.name).join(', ')}.`
        : '';
      const seriesNote = r.seriesCreated.length > 0
        ? ` Created series: ${r.seriesCreated.map((s) => s.name).join(', ')}.`
        : '';
      const permanentNote = r.recurringSeriesId && composePermanent
        ? ' Permanent series \u2014 will roll forward indefinitely.'
        : '';
      setMsg({
        kind: 'ok',
        text: `Composed ${r.eventCount} event${r.eventCount === 1 ? '' : 's'}.${venueNote}${seriesNote}${permanentNote}`
      });
      setValue(''); setHint('');
      setComposeRecurrence(''); setComposePermanent(false); setComposeDays([]);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [composeRecurrence, composePermanent, composeDays, hint]);

  const submitTextOrUrl = useCallback(async () => {
    const v = value.trim();
    if (kind === 'compose') {
      // Compose mode treats the textarea as natural-language instructions to
      // the agent and the optional `hint` field as a sourceUrl when it looks
      // URL-shaped.
      if (!v) { setMsg({ kind: 'err', text: 'Describe the event for the AI in the text area.' }); return; }
      const maybeUrl = hint.trim();
      const sourceUrl = URL_RE.test(maybeUrl) ? maybeUrl : undefined;
      await submitCompose(v, sourceUrl);
      return;
    }
    if (!v) { setMsg({ kind: 'err', text: 'Paste a URL, paste/type some text, or drop a file.' }); return; }
    const isUrl = URL_RE.test(v) && !v.includes('\n');

    if (kind === 'venue') {
      // Venue mode: URL → submit URL; otherwise treat single-line text as an explicit name.
      if (isUrl) await submitAsVenue(v);
      else await submitAsVenue(undefined, v);
      return;
    }
    if (kind === 'series' || kind === 'organizer') {
      const type: SeriesType = kind === 'organizer' ? 'organizer' : seriesType;
      if (isUrl) await submitAsSeries(v, undefined, type);
      else await submitAsSeries(undefined, v, type);
      return;
    }

    setBusy(true); setMsg(null); setLastUrl(null);
    try {
      const body = isUrl ? { url: v, hint: hint || undefined } : { text: v, hint: hint || undefined };
      const r = await api.post<IngestResponse>(eventIngestPath, body);
      setMsg({ kind: 'ok', text: `Queued for extraction (${r.detected.join(', ')}).` });
      if (isUrl) setLastUrl(v);
      setValue(''); setHint('');
      onIngested?.(r);
    } catch (e) {
      setMsg({ kind: 'err', text: formatSubmitError(e) });
    } finally {
      setBusy(false);
    }
  }, [value, hint, kind, seriesType, onIngested, submitAsVenue, submitAsSeries, submitCompose, eventIngestPath]);

  const onPaste = useCallback(async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items ?? [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          await submitFile(f);
          return;
        }
      }
    }
    // otherwise let normal text paste happen
  }, [submitFile]);

  const onDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setHover(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) await submitFile(f);
  }, [submitFile]);

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={onDrop}
        className={`p-4 mb-3 rounded border ${hover ? 'border-primary bg-light' : 'border-secondary-subtle'}`}
        style={{ borderStyle: 'dashed', borderWidth: 2 }}
        aria-label="Drop area for files"
      >
        <p className="mb-2"><strong>Drop a file, paste anything, or type a URL.</strong></p>
        <p className="text-muted small mb-3">
          Screenshots, flyers (image/PDF), Instagram/Facebook/TikTok/YouTube URLs, event-page URLs,
          venue homepages, or just paste raw text. The backend figures out what it is.
        </p>

        <fieldset className="mb-3">
          <legend className="form-label small mb-2">What is this?</legend>
          <div className="d-flex gap-3 flex-wrap" role="radiogroup" aria-label="Submission type">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-auto"
                value="auto"
                checked={kind === 'auto'}
                onChange={() => setKind('auto')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-auto">
                Auto-detect <span className="text-muted">(default — treat as event)</span>
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-event"
                value="event"
                checked={kind === 'event'}
                onChange={() => setKind('event')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-event">Event</label>
            </div>
            {isAdmin && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-venue"
                value="venue"
                checked={kind === 'venue'}
                onChange={() => setKind('venue')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-venue">
                Venue <span className="text-muted">(homepage URL or name)</span>
              </label>
            </div>
            )}
            {isAdmin && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-series"
                value="series"
                checked={kind === 'series'}
                onChange={() => setKind('series')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-series">
                Series <span className="text-muted">(recurring event or aggregator page)</span>
              </label>
            </div>
            )}
            {isAdmin && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-organizer"
                value="organizer"
                checked={kind === 'organizer'}
                onChange={() => setKind('organizer')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-organizer">
                Organizer <span className="text-muted">(promoter / collective)</span>
              </label>
            </div>
            )}
            {isAdmin && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="dz-kind"
                id="dz-kind-compose"
                value="compose"
                checked={kind === 'compose'}
                onChange={() => setKind('compose')}
                disabled={busy}
              />
              <label className="form-check-label" htmlFor="dz-kind-compose">
                Compose with AI <span className="text-muted">(describe an event in natural language)</span>
              </label>
            </div>
            )}
          </div>
        </fieldset>

        {kind === 'series' && (
          <fieldset className="mb-3">
            <legend className="form-label small mb-2">Series sub-type</legend>
            <div className="d-flex gap-3 flex-wrap" role="radiogroup" aria-label="Series sub-type">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="dz-series-type"
                  id="dz-series-recurring"
                  value="recurring-event"
                  checked={seriesType === 'recurring-event'}
                  onChange={() => setSeriesType('recurring-event')}
                  disabled={busy}
                />
                <label className="form-check-label" htmlFor="dz-series-recurring">
                  Recurring event <span className="text-muted">(e.g. weekly trivia)</span>
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="dz-series-type"
                  id="dz-series-aggregator"
                  value="aggregator"
                  checked={seriesType === 'aggregator'}
                  onChange={() => setSeriesType('aggregator')}
                  disabled={busy}
                />
                <label className="form-check-label" htmlFor="dz-series-aggregator">
                  Aggregator <span className="text-muted">(market, festival series)</span>
                </label>
              </div>
            </div>
          </fieldset>
        )}

        <label htmlFor="dz-text" className="form-label visually-hidden">Paste URL or text</label>
        <textarea
          id="dz-text"
          rows={kind === 'compose' ? 6 : 4}
          className="form-control mb-2"
          placeholder={
            kind === 'venue'
              ? 'Venue name, website URL, or Instagram / Facebook URL'
              : kind === 'series'
                ? 'https://example.com or instagram.com/handle or "Series Name"'
                : kind === 'organizer'
                  ? 'https://example.com or instagram.com/handle or "Organizer Name"'
                  : kind === 'compose'
                    ? 'Describe the event for the AI. Examples:\n  • "Add weekly trivia at OBriens every Tuesday 8pm starting next week, until I delete it."\n  • "One-time: Fall Festival at Murray Hill Park, Sat Oct 26, noon–6pm."\n  • "Monthly book club at Chamblin’s Bookmine, every 3rd Thursday 7pm."'
                    : 'https://… or paste flyer text / screenshot here'
          }
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPaste={onPaste}
          disabled={busy}
        />
        <label htmlFor="dz-hint" className="form-label small mb-1">
          {kind === 'compose' ? 'Optional source URL (flyer / Instagram / website)' : 'Optional hint for the LLM'}
        </label>
        <input
          id="dz-hint"
          type="text"
          className="form-control mb-3"
          placeholder={kind === 'compose'
            ? 'https://… (the agent reads it for additional context, optional)'
            : 'e.g. "5 Points venue, Saturday night"'}
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          disabled={busy}
        />

        {kind === 'compose' && (
          <div className="mb-3 border rounded p-2">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              aria-expanded={showComposeAdvanced}
              onClick={() => setShowComposeAdvanced((s) => !s)}
            >
              {showComposeAdvanced ? '▾' : '▸'} Advanced overrides
              <span className="text-muted ms-1">(optional — the AI infers these from your prose)</span>
            </button>
            {showComposeAdvanced && (
              <div className="mt-3">
                <div className="row g-3">
                  <div className="col-sm-6">
                    <label htmlFor="dz-rec-pattern" className="form-label small mb-1">Recurrence pattern</label>
                    <select
                      id="dz-rec-pattern"
                      className="form-select form-select-sm"
                      value={composeRecurrence}
                      onChange={(e) => setComposeRecurrence(e.target.value as typeof composeRecurrence)}
                      disabled={busy}
                    >
                      <option value="">(let AI decide)</option>
                      <option value="one-time">One-time</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div className="col-sm-6 d-flex align-items-end">
                    <div className="form-check">
                      <input
                        id="dz-rec-permanent"
                        type="checkbox"
                        className="form-check-input"
                        checked={composePermanent}
                        onChange={(e) => setComposePermanent(e.target.checked)}
                        disabled={busy}
                      />
                      <label htmlFor="dz-rec-permanent" className="form-check-label">
                        Continue indefinitely <span className="text-muted small">(rolls forward weekly until deleted)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <fieldset className="mt-3">
                  <legend className="form-label small mb-1">Days of week <span className="text-muted">(weekly / biweekly only)</span></legend>
                  <div className="d-flex flex-wrap gap-2">
                    {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const).map((d) => (
                      <div className="form-check" key={d}>
                        <input
                          id={`dz-day-${d}`}
                          type="checkbox"
                          className="form-check-input"
                          checked={composeDays.includes(d)}
                          onChange={(e) => {
                            setComposeDays((prev) => e.target.checked
                              ? [...prev, d]
                              : prev.filter((x) => x !== d));
                          }}
                          disabled={busy}
                        />
                        <label htmlFor={`dz-day-${d}`} className="form-check-label">{d}</label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            )}
          </div>
        )}
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-primary" onClick={submitTextOrUrl} disabled={busy}>
            {busy
              ? 'Submitting…'
              : kind === 'venue'
                ? 'Submit venue'
                : kind === 'series'
                  ? 'Submit series'
                  : kind === 'organizer'
                    ? 'Submit organizer'
                    : kind === 'compose'
                      ? 'Compose with AI'
                      : 'Submit URL / text'}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => fileInput.current?.click()}
            disabled={busy || kind === 'venue' || kind === 'series' || kind === 'organizer' || kind === 'compose'}
            title={(kind === 'venue' || kind === 'series' || kind === 'organizer' || kind === 'compose')
              ? 'This submission type needs text, not a file.'
              : undefined}
          >
            Choose file…
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            className="d-none"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) submitFile(f); e.target.value = ''; }}
          />
        </div>
      </div>
      {msg && (
        <div className={`alert ${msg.kind === 'ok' ? 'alert-success' : 'alert-danger'}`} role={msg.kind === 'ok' ? 'status' : 'alert'}>
          <div>{msg.text}</div>
          {lastUrl && msg.kind === 'ok' && (
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => submitAsVenue(lastUrl)}
                disabled={busy}
              >
                Add this URL as a venue too
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
